import { useState, useEffect } from 'react';
import { Form, Input, Button } from 'antd';
import {
  ArrowRightOutlined,
  MailOutlined,
  FolderOpenOutlined,
  CheckSquareOutlined,
  CloudOutlined,
  BellOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import styles from './LoginPage.module.scss';

const FEATURES = [
  {
    icon: <FolderOpenOutlined />,
    title: 'Gestion des dossiers',
    desc: "Suivez chaque dossier avec un workflow de validation complet et des rappels d'echeance automatiques.",
  },
  {
    icon: <CheckSquareOutlined />,
    title: 'Taches & activites',
    desc: "Planifiez, assignez et supervisez toutes les taches au sein de votre administration en temps reel.",
  },
  {
    icon: <CloudOutlined />,
    title: 'Stockage connecte',
    desc: "Integration native avec Google Drive et d'autres services cloud pour centraliser vos documents.",
  },
  {
    icon: <TeamOutlined />,
    title: 'Collaboration en temps réel',
    desc: 'Travaillez ensemble sur les mêmes dossiers et tâches avec des mises à jour instantanées.',
  },
  {
    icon: <BellOutlined />,
    title: 'Rappels & échéances',
    desc: 'Ne manquez plus aucune échéance grâce aux alertes intelligentes et aux notifications ciblées.',
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: 'Workflow de validation',
    desc: 'Chaque action suit un processus de validation structuré pour garantir conformité et traçabilité.',
  },
];

const STATS = [
  { value: '100%', label: 'Traçabilité' },
  { value: 'Temps réel', label: 'Collaboration' },
  { value: 'Multi-cloud', label: 'Stockage' },
];

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % FEATURES.length);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  const onFinish = (values) => {
    setLoading(true);
    window.location.href = `${import.meta.env.VITE_SSO_APP_URL}/login?key=${import.meta.env.VITE_SSO_APP_KEY}&email=${values.email}`;
  };

  return (
    <div className={`${styles.page} ${visible ? styles.pageVisible : ''}`}>
      {/* ── Panneau gauche : présentation ── */}
      <aside className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.brandRow}>
            <img src="/logo_text.png" alt="Silos" className={styles.heroLogo} />
          </div>

          <div className={styles.heroBody}>
          <h1 className={styles.heroTitle}>
            La plateforme collaborative
            <br />
            <span className={styles.heroAccent}>pour votre institution</span>
          </h1>

          <p className={styles.heroSubtitle}>
            Suivez, collaborez et validez chaque dossier et tâche avec précision — en temps réel, depuis n&apos;importe où.
          </p>

          {/* Feature carousel */}
          <div className={styles.featureBox}>
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className={`${styles.featureItem} ${i === activeFeature ? styles.featureItemActive : ''}`}
              >
                <span className={styles.featureIcon}>{f.icon}</span>
                <div>
                  <p className={styles.featureTitle}>{f.title}</p>
                  <p className={styles.featureDesc}>{f.desc}</p>
                </div>
              </div>
            ))}

            {/* Dots */}
            <div className={styles.dots}>
              {FEATURES.map((_, i) => (
                <button
                  key={i}
                  className={`${styles.dot} ${i === activeFeature ? styles.dotActive : ''}`}
                  onClick={() => setActiveFeature(i)}
                  aria-label={`Feature ${i + 1}`}
                />
              ))}
            </div>
          </div>
          </div>{/* /heroBody */}

          {/* Stats */}
          <div className={styles.stats}>
            {STATS.map((s, i) => (
              <div key={i} className={styles.stat}>
                <span className={styles.statValue}>{s.value}</span>
                <span className={styles.statLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Blobs décoratifs */}
        <div className={styles.blob1} />
        <div className={styles.blob2} />
        <div className={styles.blob3} />
      </aside>

      {/* ── Panneau droit : formulaire ── */}
      <main className={styles.formPanel}>
        <div className={styles.formCard}>
          {/* Logo visible seulement sur mobile */}
          <div className={styles.mobileHeader}>
            <img src="/logo_text.png" alt="Silos" className={styles.mobileLogo} />
          </div>

          <div className={styles.formTop}>
            <h2 className={styles.formTitle}>Bienvenue</h2>
            <p className={styles.formSubtitle}>
              Connectez-vous à votre espace institutionnel
            </p>
          </div>

          <Form
            name="login"
            onFinish={onFinish}
            layout="vertical"
            className={styles.form}
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label={<span className={styles.label}>Adresse e-mail</span>}
              rules={[
                { required: true, message: 'Veuillez entrer votre adresse e-mail' },
                { type: 'email', message: 'Adresse e-mail invalide' },
              ]}
            >
              <Input
                prefix={<MailOutlined className={styles.inputIcon} />}
                placeholder="Email institutionnel"
                className={styles.input}
                size="large"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<ArrowRightOutlined />}
                iconPosition="end"
                className={styles.submitBtn}
                loading={loading}
                size="large"
              >
                Continuer
              </Button>
            </Form.Item>
          </Form>

          <p className={styles.ssoNote}>
            Vous serez redirigé vers votre portail d&apos;authentification sécurisé.
          </p>
        </div>

        <p className={styles.footer}>
          © {new Date().getFullYear()} Silos — Plateforme institutionnelle collaborative
        </p>
      </main>
    </div>
  );
};

export default LoginPage;
